import { Link } from 'react-router-dom';
import { cleanEmailText } from '../../utils/emailText';
import { relativeTime, scaleBars } from '../../utils/homeCharts';
import './HomePanels.css';

const dayLabel = (iso) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });

export function Panel({ title, action, children, className = '' }) {
  return (
    <section className={`hp-panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}

// Tickets opened vs closed per day: paired bars with a plain-text summary
// for screen readers.
export function TicketActivityChart({ days }) {
  const created = days.map((d) => d.created);
  const closed = days.map((d) => d.closed);
  const heights = scaleBars([...created, ...closed]);
  const totalCreated = created.reduce((a, b) => a + b, 0);
  const totalClosed = closed.reduce((a, b) => a + b, 0);
  return (
    <>
      <div className="hp-legend">
        <span><i className="hp-dot hp-dot-created"></i> Opened <strong>{totalCreated}</strong></span>
        <span><i className="hp-dot hp-dot-closed"></i> Closed <strong>{totalClosed}</strong></span>
        <span className="hp-legend-note">last 14 days</span>
      </div>
      <div className="hp-bars" role="img" aria-label={`Over the last 14 days ${totalCreated} tickets were opened and ${totalClosed} closed.`}>
        {days.map((d, i) => (
          <div className="hp-bar-group" key={d.date} title={`${dayLabel(d.date)}: ${d.created} opened, ${d.closed} closed`}>
            <div className="hp-bar-pair">
              <span className="hp-bar hp-bar-created" style={{ height: `${heights[i]}%` }}></span>
              <span className="hp-bar hp-bar-closed" style={{ height: `${heights[days.length + i]}%` }}></span>
            </div>
            <span className="hp-bar-label">{new Date(d.date).getDate()}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function AttentionList({ items, onGo }) {
  if (items.length === 0) {
    return (
      <div className="hp-allclear">
        <i className="fas fa-circle-check" aria-hidden="true"></i>
        <div><strong>All clear</strong><span>Nothing is overdue, unowned or waiting on you.</span></div>
      </div>
    );
  }
  return (
    <ul className="hp-attn-list">
      {items.map((item) => {
        const body = (
          <>
            <span className={`hp-attn-icon hp-attn-${item.tone}`}><i className={`fas ${item.icon}`} aria-hidden="true"></i></span>
            <span className="hp-attn-text">{item.text}</span>
            {item.path && <i className="fas fa-chevron-right hp-attn-go" aria-hidden="true"></i>}
          </>
        );
        return (
          <li key={item.key}>
            {item.path
              ? <button type="button" className="hp-attn-row" onClick={() => onGo(item.path)}>{body}</button>
              : <div className="hp-attn-row hp-attn-static">{body}</div>}
          </li>
        );
      })}
    </ul>
  );
}

const preview = (text) => {
  const clean = cleanEmailText(text || '').reply.replace(/\s+/g, ' ').trim();
  return clean.length > 90 ? `${clean.slice(0, 90)}…` : clean;
};

export function RecentActivity({ rows }) {
  if (rows.length === 0) return <p className="hp-empty">No ticket activity yet.</p>;
  return (
    <ul className="hp-feed">
      {rows.slice(0, 6).map((r) => (
        <li key={r.id}>
          <Link to={`/tickets/${r.ticket_id}`}>
            <span className={`hp-feed-icon ${r.internal_external === 'internal' ? 'is-internal' : ''}`}>
              <i className={`fas ${r.internal_external === 'internal' ? 'fa-lock' : 'fa-envelope'}`} aria-hidden="true"></i>
            </span>
            <span className="hp-feed-main">
              <span className="hp-feed-title">#{r.ticket_number} {r.ticket_subject}</span>
              <span className="hp-feed-sub">{r.author_name}: {preview(r.description)}</span>
            </span>
            <span className="hp-feed-time">{relativeTime(r.created_at)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function WorkloadBars({ rows }) {
  if (rows.length === 0) return <p className="hp-empty">No open tickets.</p>;
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <ul className="hp-workload">
      {rows.slice(0, 6).map((r) => (
        <li key={r.name}>
          <span className="hp-workload-name">{r.name}</span>
          <span className="hp-workload-track"><span className={`hp-workload-fill${r.name === 'Unassigned' ? ' is-unassigned' : ''}`} style={{ width: `${Math.max(6, (r.count / max) * 100)}%` }}></span></span>
          <span className="hp-workload-count">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

export function MonthBars({ months, format }) {
  const heights = scaleBars(months.map((m) => Math.max(0, m.value)));
  return (
    <div className="hp-months" role="img" aria-label={`Profit by month: ${months.map((m) => `${m.label} ${format(m.value)}`).join(', ')}`}>
      {months.map((m, i) => (
        <div className="hp-month" key={m.label} title={`${m.label}: ${format(m.value)}`}>
          <span className="hp-month-value">{format(m.value)}</span>
          <div className="hp-month-track"><span className={`hp-month-bar${m.value < 0 ? ' is-negative' : ''}`} style={{ height: `${heights[i]}%` }}></span></div>
          <span className="hp-bar-label">{m.label}</span>
        </div>
      ))}
    </div>
  );
}

export function KpiPills({ stats }) {
  const pills = [];
  if (stats.firstResponseOnTargetRate !== null && stats.firstResponseOnTargetRate !== undefined) pills.push({ label: 'First reply on target', value: `${stats.firstResponseOnTargetRate}%` });
  if (stats.onTimeRate !== null && stats.onTimeRate !== undefined) pills.push({ label: 'Closed on time', value: `${stats.onTimeRate}%` });
  if (stats.avgFirstResponseHours !== null && stats.avgFirstResponseHours !== undefined) pills.push({ label: 'Avg first reply', value: `${stats.avgFirstResponseHours < 1 ? `${Math.round(stats.avgFirstResponseHours * 60)}m` : `${stats.avgFirstResponseHours.toFixed(1)}h`}` });
  if (pills.length === 0) return null;
  return (
    <div className="hp-kpis">
      {pills.map((p) => <div key={p.label} className="hp-kpi"><strong>{p.value}</strong><span>{p.label}</span></div>)}
    </div>
  );
}
